import { DOCUMENT, NgComponentOutlet, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  Signal,
  afterRenderEffect,
  computed,
  inject,
  isDevMode,
  signal
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';

import { DeployChannelCountService } from '@gfe/core/services/deploy-plan-config/deploy-channel-count.service';

import { AssistantService } from '@core/services/assistant/assistant.service';
import { ICONS } from '@core/services/icons/icons.service';
import { FindingsVisibilityService } from '@core/services/plan-findings/findings-visibility.service';

import { AnchoredPopupStackService } from '@shared/services/anchored-popup/anchored-popup-stack.service';

import { planPanelHostClass, planPanelPlaceholderClass } from './plan-panel-host-class';
import { PLAN_PANEL_SECTIONS, PlanPanelSection } from './plan-panel-section';
import { PlanPanelService } from './plan-panel.service';

interface SectionEntry {
  section: PlanPanelSection;
  visible: Signal<boolean>;
  collapsed: Signal<boolean>;
}

/** Collapsed, the right column stacks below the left, so it must also come after it in `order`. */
function reportMisorderedColumns(sections: PlanPanelSection[]): void {
  const lastLeft = Math.max(
    ...sections
      .filter((section) => section.expandedColumn !== 'right')
      .map((section) => section.order)
  );
  const early = sections.filter(
    (section) => section.expandedColumn === 'right' && section.order <= lastLeft
  );
  if (early.length === 0) return;
  console.error(
    `Plan panel: right-column section(s) ${early.map((section) => `"${section.id}"`).join(', ')} ` +
      `must have a higher order than every left-column section.`
  );
}

@Component({
  selector: 'app-plan-panel',
  imports: [NgComponentOutlet, NgTemplateOutlet, TranslatePipe],
  templateUrl: './plan-panel.component.html',
  // Here rather than on a section, which remounts on every uncollapse and would refetch the list.
  providers: [DeployChannelCountService],
  host: {
    class: 'contents',
    '(keydown.escape)': 'onEscape($event)'
  },
  styles: ``
})
export class PlanPanelComponent {
  protected readonly panel = inject(PlanPanelService);
  private readonly assistant = inject(AssistantService);
  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly popups = inject(AnchoredPopupStackService);
  private readonly document = inject(DOCUMENT);
  protected readonly ICONS = ICONS;

  private readonly entries: SectionEntry[] = (() => {
    const injector = inject(Injector);
    const sections = inject(PLAN_PANEL_SECTIONS, { optional: true }) ?? [];
    const sorted = [...sections].sort((a, b) => a.order - b.order);
    if (isDevMode()) reportMisorderedColumns(sorted);
    return sorted.map((section) => ({
      section,
      visible: section.visible?.(injector) ?? signal(true),
      collapsed: this.panel.isCollapsed(section.id)
    }));
  })();

  protected readonly visibleEntries = computed(() =>
    this.entries.filter((entry) => entry.visible())
  );
  /** Both columns stay rendered, stacked while collapsed, so expanding never remounts a section. */
  protected readonly leftEntries = computed(() =>
    this.visibleEntries().filter((entry) => entry.section.expandedColumn !== 'right')
  );
  protected readonly rightEntries = computed(() =>
    this.visibleEntries().filter((entry) => entry.section.expandedColumn === 'right')
  );

  private readonly assistantOpen = computed(
    () => this.assistant.toggleVisible() && this.assistant.panelOpen()
  );
  protected readonly hostClass = computed(() =>
    planPanelHostClass({
      expanded: this.panel.expanded(),
      assistantOpen: this.assistantOpen()
    })
  );
  protected readonly placeholderClass = computed(() =>
    planPanelPlaceholderClass({ assistantOpen: this.assistantOpen() })
  );
  protected readonly bodyClass = computed(() =>
    this.panel.expanded()
      ? 'grid min-h-0 flex-1 grid-cols-[2fr_3fr]'
      : 'flex min-h-0 flex-1 flex-col overflow-y-auto'
  );
  protected readonly leftColumnClass = computed(() =>
    this.panel.expanded() ? 'border-weak min-h-0 overflow-y-auto border-r' : ''
  );
  protected readonly rightColumnClass = computed(() =>
    this.panel.expanded() ? 'min-h-0 overflow-y-auto' : ''
  );

  constructor() {
    inject(DestroyRef).onDestroy(inject(FindingsVisibilityService).show());

    afterRenderEffect(() => {
      const request = this.panel.revealRequest();
      const visible = this.visibleEntries();
      if (!request) return;
      if (!visible.some((entry) => entry.section.id === request.sectionId)) {
        this.panel.revealHandled(request.seq);
        return;
      }
      const frame = Array.from(
        this.host.nativeElement.querySelectorAll<HTMLElement>('[data-section-id]')
      ).find((element) => element.dataset['sectionId'] === request.sectionId);
      if (!frame) return;
      frame.scrollIntoView({ block: 'start' });
      this.panel.revealHandled(request.seq);
    });
  }

  /**
   * Closing removes the focused button with the panel, so focus moves to the top bar's toggle,
   * which is always there while a plan is in view, rather than falling back to the page body.
   */
  protected close(): void {
    this.panel.toggle();
    this.document.getElementById('plan-panel-toggle')?.focus();
  }

  /**
   * An open popup (menu, listbox, dropdown, card) takes Escape for itself; its trigger keeps
   * focus. A card's trigger carries no `aria-expanded`, so the anchored-popup stack is asked
   * too: a dismissed popup leaves the stack only when it is destroyed, after this keydown has
   * finished dispatching.
   */
  protected onEscape(event: Event): void {
    if (!this.panel.expanded() || this.popups.hasOpen()) return;
    const target = event.target as Element;
    if (
      target.closest(
        '[aria-expanded="true"][aria-haspopup], [role="combobox"][aria-expanded="true"]'
      )
    )
      return;
    this.panel.expanded.set(false);
  }
}
